/**
 * usePortalForm — loads portal + workflow context for form rendering.
 *
 * Follows FERP hook pattern: consumes service singleton, manages React state.
 * UI components use this hook to get the form schema for rendering.
 */
import { useState, useEffect, useCallback } from 'react';
import type { ExposedWorkflow, Portal } from '../types';
import { portalFormService, type PortalFormContext } from '../services/PortalFormService';

interface UsePortalFormReturn {
  portal: Portal | null;
  workflow: ExposedWorkflow | null;
  isLoading: boolean;
  error: Error | null;
  reload: () => void;
}

export function usePortalForm(portalSlug: string, workflowSlug: string): UsePortalFormReturn {
  const [context, setContext] = useState<PortalFormContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadForm = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await portalFormService.loadFormContext(portalSlug, workflowSlug);
      setContext(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [portalSlug, workflowSlug]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  return {
    portal: context?.portal ?? null,
    workflow: context?.workflow ?? null,
    isLoading,
    error,
    reload: loadForm,
  };
}
